/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { useCallback, useEffect, useState } from 'react';

import has from 'lodash/has';
import omit from 'lodash/omit';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import { Button, Checkbox, LinkBlock } from '@components/common';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import Modal from '@modules/modal/components/Modal';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { useTableBasketApi } from '@modules/table/api';
import type { ITableBasketItem } from '@modules/table/types';

import {
    BasketModalContainerStyled,
    BasketModalItemStyled,
    BasketModalListStyled,
    BasketModalTitleCounterStyled,
} from './BasketModalStyled';

interface ITableBasketModal extends Pick<IModalRenderProps, 'onCloseModal'> {
    title: string;
}

const TableBasketModal = ({ title, onCloseModal }: ITableBasketModal) => {

    const { basket: tBasket, modal: tModal } = useSelector(makeSelectTranslationCommon());

    const { count, data, getBasketList, deleteItemsFromBasket, ...infiniteScroll } = useTableBasketApi();

    const [removedItems, setRemoveItems] = useState<{ [itemId: string]: ITableBasketItem }>({});

    useEffect(() => {
        getBasketList(0)
    }, []);

    const onApply = () => {
        if (Object.keys(removedItems).length) {
            deleteItemsFromBasket(removedItems);
        }
        onCloseModal();
    };

    const onCheckAll = (checked: boolean) => {
        setRemoveItems(checked
            ? {}
            : (data?.reduce((items: { [itemId: string]: ITableBasketItem }, item: ITableBasketItem) => {
                items[item.id] = item;
                return items;
            }, {})) ?? {});
    };

    const RenderItem = useCallback((item: ITableBasketItem) => (
        <BasketModalItemStyled key={item.id}>
            <Checkbox
                checked={!has(removedItems, item.id)}
                label={item.label}
                onChange={checked => setRemoveItems(checked
                    ? omit(removedItems, item.id)
                    : { ...removedItems, [item.id]: item })
                }
            />
        </BasketModalItemStyled>
    ), [removedItems]);

    return (
        <Modal withoutPadding dataName="basket-container" width="800px">

            <Modal.Title>
                {title}
                <BasketModalTitleCounterStyled>
                    {tBasket.counterFrom} {count - Object.keys(removedItems).length} {tBasket.counterTo} {count}
                </BasketModalTitleCounterStyled>
            </Modal.Title>

            <BasketModalContainerStyled>
                <BasketModalItemStyled>
                    <Checkbox
                        checked={Object.keys(removedItems).length === 0}
                        label={tBasket.selectAll}
                        onChange={onCheckAll}
                    />
                </BasketModalItemStyled>
            </BasketModalContainerStyled>

            <BasketModalListStyled>
                <InfiniteScroll
                    {...infiniteScroll}
                    loadMore={getBasketList}
                    useWindow={false}
                >
                    {data?.map(RenderItem)}
                    {(!data || data.length === 0) && (
                        <BasketModalItemStyled>
                            {tBasket.noSearchMatcher}
                        </BasketModalItemStyled>
                    )}
                </InfiniteScroll>
            </BasketModalListStyled>

            <Modal.Control>
                <Button kind="medium" onClick={onApply}>
                    {tModal.apply}
                </Button>
                <LinkBlock onClick={onCloseModal}>
                    {tModal.close}
                </LinkBlock>
            </Modal.Control>
        </Modal>
    );
};

export default TableBasketModal;
